import React from "react";
import { useTheme } from "../contexts/ThemeContext";
import SEOHead from "./SEOHead";

const UPIButton = () => {
    var userAgent = navigator.userAgent || navigator.vendor || window.opera;

    if (
        ((/windows phone/i.test(userAgent) ||
        /android/i.test(userAgent) ||
        /iPad|iPhone|iPod/.test(userAgent)) && !window.MSStream)
    ) {
        return (
            <div className="mb-6">
                <a
                    className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-green-500 to-blue-500 text-white font-semibold rounded-xl hover:shadow-lg transition-all duration-300 transform hover:scale-105"
                    href="upi://pay?mode=02&pa=Q571045502@ybl&pn=Avant-GardeX&purpose=00&mc=8999&orgid=180001&sign=MEQCIBHmiMVoXSPOaP6L+qxi1ptp1Ruz9dESTlf8wGpEoaJOAiAnA1H+LS/H69bRQt1SBwQd3kjZfc5lrdG45BQCiUbpcA=="
                >
                    💳 Pay with UPI
                </a>
            </div>
        );
    }
    return null;
}

const Donations = () => {
    const { colors } = useTheme();

    return (
        <>
            <SEOHead
                title="Support Collegia — Buy Us a Coffee"
                description="Support the development of Collegia, the academics manager trusted by 50,000+ university students. Help us maintain free, privacy-first services and open-source tools."
                keywords="support Collegia, donate, Collegia app, student app funding, buy me a coffee, open source, university app, academic tools"
                url="https://collegia.app/donate"
            />
            <div className={`${colors.bg} min-h-screen py-8 px-4`}>
            <div className="max-w-4xl mx-auto">
                {/* Header Section */}
                <div className="text-center mb-12">
                    <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-green-500 to-blue-500 rounded-full mb-8 animate-pulse">
                        <span className="text-3xl">☕</span>
                    </div>
                    <h1 className={`text-4xl md:text-6xl font-bold mb-4 ${colors.text} bg-gradient-to-r from-green-400 to-blue-600 bg-clip-text text-transparent`}>
                        Buy us a coffee
                    </h1>
                    <p className={`text-xl ${colors.textSecondary} font-light mb-2`}>
                        Supporting independent student innovation
                    </p>
                    <p className={`${colors.textMuted} text-lg`}>
                        Help us maintain this free, privacy-first service for all students
                    </p>
                </div>

                {/* Main Content */}
                <div className="grid md:grid-cols-2 gap-12 items-center">
                    {/* Left Side - Information */}
                    <div className="space-y-8">
                        {/* Student Development */}
                        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm`}>
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                    <span className="text-blue-500">👨‍🎓</span>
                                </div>
                                <div>
                                    <h3 className={`text-xl font-semibold mb-3 ${colors.text}`}>
                                        Student-Built, Student-Funded
                                    </h3>
                                    <p className={`${colors.textSecondary} leading-relaxed`}>
                                        This app is <span className={`${colors.text} font-semibold`}>independently developed by students</span>, 
                                        not by the university. We've invested years of work to provide a better interface 
                                        for accessing academic information while maintaining complete privacy.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm border-green-500/30 bg-green-500/5`}>
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-green-500/20 rounded-full flex items-center justify-center">
                                    <span className="text-green-500">🔒</span>
                                </div>
                                <div>
                                    <h3 className={`text-xl font-semibold mb-3 ${colors.text}`}>
                                        Privacy-First Approach
                                    </h3>
                                    <p className={`${colors.textSecondary} leading-relaxed`}>
                                        Your donations support a <span className={`${colors.text} font-semibold`}>truly private app</span> - 
                                        we don't store, cache, or transmit any of your university data. Everything processes 
                                        locally on your device, keeping your academic information completely secure.
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm`}>
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                                    <span className="text-purple-500">💰</span>
                                </div>
                                <div>
                                    <h3 className={`text-xl font-semibold mb-3 ${colors.text}`}>
                                        What Your Donation Supports
                                    </h3>
                                    <ul className={`${colors.textSecondary} space-y-2`}>
                                        <li className="flex items-center">
                                            <span className="w-2 h-2 bg-green-500 rounded-full mr-3"></span>
                                            App hosting and infrastructure costs
                                        </li>
                                        <li className="flex items-center">
                                            <span className="w-2 h-2 bg-blue-500 rounded-full mr-3"></span>
                                            Continued development and improvements
                                        </li>
                                        <li className="flex items-center">
                                            <span className="w-2 h-2 bg-purple-500 rounded-full mr-3"></span>
                                            Security certificates and compliance
                                        </li>
                                        <li className="flex items-center">
                                            <span className="w-2 h-2 bg-orange-500 rounded-full mr-3"></span>
                                            Keeping the service completely free
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        {/* Transparency */}
                        <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm border-blue-500/30 bg-blue-500/5`}>
                            <div className="flex items-start space-x-4">
                                <div className="flex-shrink-0 w-8 h-8 bg-blue-500/20 rounded-full flex items-center justify-center">
                                    <span className="text-blue-500">📊</span>
                                </div>
                                <div>
                                    <h3 className={`text-xl font-semibold mb-3 ${colors.text}`}>
                                        Complete Transparency
                                    </h3>
                                    <p className={`${colors.textSecondary} leading-relaxed`}>
                                        Every donation goes directly toward operational costs. We're not profiting from this service - 
                                        our goal is simply to <span className={`${colors.text} font-semibold`}>keep this essential tool available</span> 
                                        for the student community.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* UPI Button for mobile devices */}
                        <div className="text-center">
                            <UPIButton />
                        </div>
                    </div>

                    {/* Right Side - QR Code */}
                    <div className="text-center">
                        <div className={`${colors.card} rounded-3xl p-8 border ${colors.border} backdrop-blur-sm shadow-2xl`}>
                            <h3 className={`text-2xl font-semibold mb-6 ${colors.text}`}>
                                Scan to Donate 📱
                            </h3>
                            
                            <div className="relative inline-block">
                                <div className="absolute inset-0 bg-gradient-to-r from-green-400 to-blue-500 rounded-2xl blur opacity-20"></div>
                                <div className="relative bg-white p-4 rounded-2xl shadow-lg">
                                    <img
                                        src="assets/qrcode.png"
                                        className="w-64 h-64 md:w-80 md:h-80 mx-auto rounded-xl"
                                        alt="Donation QR Code"
                                    />
                                </div>
                            </div>
                            
                            <p className={`${colors.textSecondary} mt-6 text-sm`}>
                                UPI ID: <span className={`${colors.text} font-mono bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded`}>Q571045502@ybl</span>
                            </p>
                        </div>
                    </div>
                </div>

                {/* Our Commitment Section */}
                <div className={`${colors.card} rounded-2xl p-8 border ${colors.border} backdrop-blur-sm mt-12 mb-8`}>
                    <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-8 h-8 bg-purple-500/20 rounded-full flex items-center justify-center">
                            <span className="text-purple-500">🤝</span>
                        </div>
                        <div>
                            <h3 className={`text-2xl font-semibold mb-4 ${colors.text}`}>
                                Our Commitment to the Community
                            </h3>
                            <div className={`${colors.textSecondary} leading-relaxed space-y-4`}>
                                <p>
                                    As independent student developers, we're committed to:
                                </p>
                                <div className="grid md:grid-cols-2 gap-4 mt-6">
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-green-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Maintaining privacy:</strong> Your data stays on your device</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Keeping it free:</strong> No subscriptions or premium features</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-purple-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Transparent operations:</strong> Open about our practices</span>
                                    </div>
                                    <div className="flex items-start space-x-3">
                                        <span className="w-2 h-2 bg-orange-500 rounded-full mt-2 flex-shrink-0"></span>
                                        <span><strong>Continuous improvement:</strong> Regular updates and fixes</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Thank You Section */}
                <div className="text-center mt-16 pt-12 border-t border-gray-700/20">
                    <h3 className={`text-3xl font-bold mb-4 ${colors.text}`}>
                        Thank You! 🙏
                    </h3>
                    <p className={`${colors.textSecondary} text-lg mb-8 max-w-2xl mx-auto`}>
                        Your support enables us to continue providing this secure, privacy-first service. 
                        Every contribution helps thousands of students have a better academic experience 
                        while keeping their data completely private.
                    </p>
                    
                    <div className="flex justify-center space-x-4">
                        <a 
                            href="/ads" 
                            className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                        >
                            💰 Why we need support
                        </a>
                        <a 
                            href="/university_response" 
                            className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                        >
                            🤝 Our Approach
                        </a>
                        <a 
                            href="/" 
                            className={`inline-flex items-center px-6 py-3 ${colors.buttonSecondary} font-semibold rounded-lg hover:shadow-lg transition-all duration-300`}
                        >
                            🏠 Back to Home
                        </a>
                    </div>
                </div>

                {/* Footer Message */}
                <div className="text-center mt-12">
                    <p className={`${colors.textMuted} text-sm`}>
                        Made with ❤️ by independent students, for students
                    </p>
                </div>
            </div>
        </div>
        </>
    );
};

export default Donations;
